import * as React from "react";
import { cva } from "class-variance-authority";
import { Textarea, type TextareaProps } from "./textarea";
import { cn } from "../../lib/utils";

const labelVariants = cva(
  "ui-absolute ui-left-2.5 ui-text-gray-medium ui-transition-all ui-duration-200 ui-pointer-events-none",
  {
    variants: {
      state: {
        default: "ui-text-sm ui-top-[50%] ui-translate-y-[-50%]",
        shrunk: "ui-top-0 ui-translate-y-0 ui-text-text-xxs-medium",
      },
    },
    defaultVariants: {
      state: "default",
    },
  }
);

export interface BaseTextAreaProps
  extends Omit<TextareaProps, "label" | "variant"> {
  label?: string;
  variant?: "standard" | "ghost";
  textAreaClassName?: string;
}

const BaseTextArea = React.forwardRef<HTMLTextAreaElement, BaseTextAreaProps>(
  ({ className, label, value, defaultValue, variant = "standard", textAreaClassName, ...props }, ref) => {
    const hasLabel = Boolean(label);
    const BASE_HEIGHT_PX = hasLabel ? 47 : 30; // 47px with label, 30px without
    const [focused, setFocused] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState(
      value ?? defaultValue ?? ""
    );
    const textAreaRef = React.useRef<HTMLTextAreaElement | null>(null);
    const hasValue = Boolean(internalValue) || Boolean(value);
    const shouldShrinkLabel = focused || hasValue;

    const textAreaValue = value !== undefined ? value : internalValue;

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (value === undefined) {
        setInternalValue(e.target.value);
      }
      props.onChange?.(e);
    };

    const setRefs = (node: HTMLTextAreaElement | null) => {
      textAreaRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current =
          node;
      }
    };

    const adjustHeight = () => {
      const el = textAreaRef.current;
      if (!el) return;
      el.style.minHeight = `${BASE_HEIGHT_PX}px`;
      el.style.height = "auto";
      const nextHeight = Math.max(el.scrollHeight, BASE_HEIGHT_PX);
      el.style.height = `${nextHeight}px`;
    };

    React.useEffect(() => {
      adjustHeight();
    }, [textAreaValue]);

    // Adjust height on mount to handle prefilled content
    // Use setTimeout to ensure DOM has rendered the text
    React.useEffect(() => {
      const timer = setTimeout(() => {
        adjustHeight();
      }, 0);
      return () => clearTimeout(timer);
    }, []);

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setFocused(false);
      props.onBlur?.(e);
    };

    const textAreaId = props.id || `textarea-${React.useId()}`;
    const labelId = label ? `label-${textAreaId}` : undefined;

    return (
      <div className={cn("ui-relative ui-w-full", className)}>
        <Textarea
          {...props}
          id={textAreaId}
          ref={setRefs}
          variant={variant}
          value={textAreaValue}
          style={{ minHeight: BASE_HEIGHT_PX }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder={
            hasLabel
              ? shouldShrinkLabel
                ? props.placeholder
                : undefined
              : props.placeholder
          }
          aria-labelledby={labelId}
          className={cn("ui-pb-2 ui-text-text-sm-regular ui-leading-normal", hasLabel && "ui-pt-4", textAreaClassName)}
        />
        {label && (
          <label
            htmlFor={textAreaId}
            id={labelId}
            className={cn(
              labelVariants({
                state: shouldShrinkLabel ? "shrunk" : "default",
              }),
              focused && shouldShrinkLabel && "ui-text-primary-dark",
              props.disabled && "ui-opacity-50",
              props.error && "ui-text-red-dark"
            )}
          >
            {label}
            {props.required && " *"}
          </label>
        )}
      </div>
    );
  }
);

BaseTextArea.displayName = "BaseTextArea";

export { BaseTextArea };
